// src/components/winback/WinbackActivationChecklist.jsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCircle, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useWinbackConfig } from '../../hooks/useWinbackConfig';
import { useWinbackStats } from '../../hooks/useWinbackStats';
import { get } from '../../helpers/apiHelper';
import { useNavigate } from 'react-router-dom';

const WinbackActivationChecklist = ({ marcaId }) => {
    const navigate = useNavigate();
    const { config, loading: configLoading } = useWinbackConfig(marcaId);
    const { stats, loading: statsLoading } = useWinbackStats(marcaId);
    const [hasAnyClients, setHasAnyClients] = useState(null);
    const [loadingClients, setLoadingClients] = useState(true);

    // Verificar si hay clientes en la marca
    useEffect(() => {
        const checkClients = async () => {
            if (!marcaId) {
                setLoadingClients(false);
                return;
            }
            try {
                const clientes = await get(`marcas/${marcaId}/clientes`);
                setHasAnyClients(clientes && clientes.length > 0);
            } catch (err) {
                console.error('Error checking clients:', err);
                setHasAnyClients(null);
            } finally {
                setLoadingClients(false);
            }
        };
        checkClients();
    }, [marcaId]);

    const [isCompleted, setIsCompleted] = useState(false);

    useEffect(() => {
        // Verificar si todas las tareas están completadas
        const configDone = config?.enabled === true;
        const hasClients = hasAnyClients === true;
        const hasSentCampaign = stats?.sent > 0;

        setIsCompleted(configDone && hasClients && hasSentCampaign);
    }, [config, hasAnyClients, stats]);

    const steps = [
        {
            id: 'config',
            label: 'Configura tu WinBack',
            completed: config?.enabled === true,
            description: 'Activa el sistema y ajusta los umbrales',
            action: () => {
                // Scroll a la sección de configuración
                const configSection = document.getElementById('winback-config-section');
                if (configSection) {
                    configSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        },
        {
            id: 'data',
            label: 'Agrega tus primeros clientes',
            completed: hasAnyClients === true,
            description: hasAnyClients === true
                ? 'Ya tienes clientes en tu marca'
                : 'Necesitas agregar clientes a tu marca para empezar',
            action: () => {
                navigate('/clientes/crear');
            }
        },
        {
            id: 'send',
            label: 'Envía tu primer WinBack',
            completed: stats?.sent > 0,
            description: 'Prueba el sistema con clientes de alta urgencia',
            action: () => {
                const customersSection = document.getElementById('inactive-customers-section');
                if (customersSection) {
                    customersSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        }
    ];

    const allCompleted = steps.every(step => step.completed);

    if (allCompleted) {
        return null; // No mostrar si todo está completo
    }

    return (
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl shadow-lg p-6 mb-8 text-white">
            <div className="flex items-start justify-between mb-4">
                <div>
                    <h3 className="text-xl font-bold mb-2">
                        Para activar WinBack y empezar a recuperar clientes:
                    </h3>
                    <p className="text-purple-100 text-sm">
                        Completa estos pasos para empezar a recuperar clientes perdidos automáticamente
                    </p>
                </div>
            </div>

            <div className="space-y-3">
                {steps.map((step, index) => (
                    <div
                        key={step.id}
                        className={`flex items-start p-4 rounded-lg transition-all ${step.completed
                            ? 'bg-white bg-opacity-20'
                            : 'bg-white bg-opacity-10 hover:bg-opacity-15 cursor-pointer'
                            }`}
                        onClick={!step.completed ? step.action : undefined}
                    >
                        <div className="flex-shrink-0 mr-4 mt-1">
                            {step.completed ? (
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    className="text-green-300 text-xl"
                                />
                            ) : (
                                <FontAwesomeIcon
                                    icon={faCircle}
                                    className="text-white text-xl opacity-50"
                                />
                            )}
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className={`font-medium ${step.completed ? 'line-through opacity-75' : ''}`}>
                                        {index + 1}️⃣ {step.label}
                                    </p>
                                    <p className="text-purple-100 text-sm mt-1">
                                        {step.description}
                                    </p>
                                </div>
                                {!step.completed && (
                                    <FontAwesomeIcon
                                        icon={faArrowRight}
                                        className="text-white opacity-50 ml-4"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {!allCompleted && hasAnyClients === false && (
                <div className="mt-4 pt-4 border-t border-white border-opacity-20">
                    <p className="text-purple-100 text-sm">
                        💡 <strong>Tip:</strong> Una vez que agregues clientes y registres sus visitas o compras, podremos detectar automáticamente quién está inactivo y necesita un WinBack.
                    </p>
                </div>
            )}
        </div>
    );
};

export default WinbackActivationChecklist;

